import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  AlertColor,
  TableHead,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { BaseSelectionModal } from "./BaseSelectionModal";
import { useNavigate, useParams } from "react-router-dom";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { formatDate } from "../../../common/const";
import RequiredLabel from "../../atoms/RequiredLabel";
import ConfirmDialog from "../../molecules/ConfirmDialog";

const connectionReminderAlertPeriodSteps: number[] = [
  0, 5, 10, 30, 60, 120, 360, 720, 1440,
]; // minutes

// 1日から30日の分単位の配列を生成し、「オフ」の値を追加
const connectionReminderAlertPeriodStepsDay: number[] = [
  0,
  ...Array.from({ length: 30 }, (_, i) => (i + 1) * 1440),
]; // 0 は「オフ」、1日=1440分

type BuildingEditFormProps = {
  building: any;
  buildingManagementId: string;
  setBuildingManagementId: React.Dispatch<React.SetStateAction<string>>;
  buildingName: string;
  setBuildingName: React.Dispatch<React.SetStateAction<string>>;
  buildingAddress: string;
  setBuildingAddress: React.Dispatch<React.SetStateAction<string>>;
  createdAt: string | undefined;
  memo: string;
  setMemo: React.Dispatch<React.SetStateAction<string>>;
  isConnectionAlertEnabled: boolean;
  setConnectionAlertEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  connectionAlertEmail: string;
  setConnectionAlertEmail: React.Dispatch<React.SetStateAction<string>>;
  connectionRecoveryAlertPeriod: number;
  setConnectionRecoveryAlertPeriod: React.Dispatch<
    React.SetStateAction<number>
  >;
  connectionAlertPeriod: number;
  setConnectionAlertPeriod: React.Dispatch<React.SetStateAction<number>>;
  connectionReminderAlertPeriod: number;
  setConnectionReminderAlertPeriod: React.Dispatch<
    React.SetStateAction<number>
  >;
  isAnomalyAlertEnabled: boolean;
  setAnomalyAlertEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  anomalyAlertEmail: string;
  setAnomalyAlertEmail: React.Dispatch<React.SetStateAction<string>>;
  isAnomalyUnresponsiveOpenAlertEnabled: boolean;
  setAnomalyUnresponsiveOpenAlertEnabled: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  isAnomalyUnresponsiveLockAlertEnabled: boolean;
  setAnomalyUnresponsiveLockAlertEnabled: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  isDoorStackedAlertEnabled: boolean;
  setIsDoorStackedAlertEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isCannotAllCloseAlertEnabled: boolean;
  setIsCannotAllCloseAlertEnabled: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  isCannotAnswerBackOnAlertEnabled: boolean;
  setIsCannotAnswerBackOnAlertEnabled: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  isCannotAnswerBackOffAlertEnabled: boolean;
  setIsCannotAnswerBackOffAlertEnabled: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  fulltechOfficeName: any;
  fulltechOfficeId: any;
  setFulltechOfficeName: React.Dispatch<React.SetStateAction<any>>;
  setFulltechOfficeId: React.Dispatch<React.SetStateAction<any>>;
  buildingId: string | undefined;
  baseModalOpen: boolean;
  setBaseModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
};

export const BuildingEditFrom: React.FC<BuildingEditFormProps> = ({
  buildingManagementId,
  setBuildingManagementId,
  buildingName,
  setBuildingName,
  buildingAddress,
  setBuildingAddress,
  createdAt,
  memo,
  setMemo,
  isConnectionAlertEnabled,
  setConnectionAlertEnabled,
  connectionAlertEmail,
  setConnectionAlertEmail,
  connectionRecoveryAlertPeriod,
  setConnectionRecoveryAlertPeriod,
  connectionAlertPeriod,
  setConnectionAlertPeriod,
  connectionReminderAlertPeriod,
  setConnectionReminderAlertPeriod,
  isAnomalyAlertEnabled,
  setAnomalyAlertEnabled,
  anomalyAlertEmail,
  setAnomalyAlertEmail,

  isAnomalyUnresponsiveOpenAlertEnabled,
  setAnomalyUnresponsiveOpenAlertEnabled,
  isAnomalyUnresponsiveLockAlertEnabled,
  setAnomalyUnresponsiveLockAlertEnabled,
  isDoorStackedAlertEnabled,
  setIsDoorStackedAlertEnabled,
  isCannotAllCloseAlertEnabled,
  setIsCannotAllCloseAlertEnabled,
  isCannotAnswerBackOnAlertEnabled,
  setIsCannotAnswerBackOnAlertEnabled,
  isCannotAnswerBackOffAlertEnabled,
  setIsCannotAnswerBackOffAlertEnabled,
  fulltechOfficeName,
  setFulltechOfficeName,
  fulltechOfficeId,
  setFulltechOfficeId,
  buildingId,
  baseModalOpen,
  setBaseModalOpen,
  setSnackbarOpen,
  setAlertSeverity,
  setSnackbarMessage,
}) => {
  const { autoBuildingId } = useParams();

  const navigate = useNavigate();

  // 分単位の状態を追加 (useStateの初期値は適宜設定してください)
  const [
    connectionRecoveryAlertPeriodMinutes,
    setConnectionRecoveryAlertPeriodMinutes,
  ] = useState(connectionRecoveryAlertPeriod / 60);
  const [connectionAlertPeriodMinutes, setConnectionAlertPeriodMinutes] =
    useState(connectionAlertPeriod / 60);
  const [
    connectionReminderAlertPeriodMinutes,
    setConnectionReminderAlertPeriodMinutes,
  ] = useState(connectionReminderAlertPeriod / 60);

  // useEffect内でAPIから取得した値（秒）を分に変換して状態を更新
  useEffect(() => {
    setConnectionRecoveryAlertPeriodMinutes(connectionRecoveryAlertPeriod / 60);
    setConnectionAlertPeriodMinutes(connectionAlertPeriod / 60);
    setConnectionReminderAlertPeriodMinutes(connectionReminderAlertPeriod / 60);
  }, [
    connectionRecoveryAlertPeriod,
    connectionAlertPeriod,
    connectionReminderAlertPeriod,
  ]);

  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 確認ダイアログ
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deletedConfirmOpen, setDeletedConfirmOpen] = React.useState(false);

  // 各フィールドが変更されたかどうかを追跡する状態
  const [isTouched, setIsTouched] = useState({
    buildingManagementId: false,
    buildingName: false,
    buildingAddress: false,
    fulltechOfficeId: false,
    memo: false,
  });

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  const [validationErrors, setValidationErrors] = useState({
    buildingManagementId: "",
    buildingName: "",
    buildingAddress: "",
    fulltechOfficeId: "",
    memo: "",
  });

  // 入力が変更されたときのハンドラ
  const handleBlur = (field: string) => {
    setIsTouched({
      ...isTouched,
      [field]: true,
    });
  };

  useEffect(() => {
    const errors = {
      buildingManagementId:
        isTouched.buildingManagementId &&
        buildingManagementId !== "" &&
        buildingManagementId.length > CHARACTER_LIMIT
          ? `取付先コードは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      buildingName:
        isTouched.buildingName &&
        buildingName !== "" &&
        buildingName.length > CHARACTER_LIMIT
          ? `取付先名（物件名称）は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      buildingAddress:
        isTouched.buildingAddress &&
        buildingAddress !== "" &&
        buildingAddress.length > CHARACTER_LIMIT
          ? `物件住所は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      fulltechOfficeId:
        isTouched.fulltechOfficeId && !fulltechOfficeId
          ? "管轄拠点を選択してください"
          : "",
      memo:
        isTouched.memo && memo !== "" && memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
    };

    setValidationErrors(errors);
  }, [
    buildingManagementId,
    buildingName,
    buildingAddress,
    memo,
    isTouched,
    fulltechOfficeId,
  ]);

  const patchBuildingData = async (
    buildingManagementId: string,
    buildingName: string,
    buildingAddress: string,
    autoFulltechOfficeId: number,
    memo?: string
  ) => {
    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    // call updateBuilding API
    const response = await fetch(
      process.env.REACT_APP_API_ENDPOINT + `/buildings/${autoBuildingId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: jwtToken, // Authorizationヘッダーを追加
        },
        body: JSON.stringify({
          buildingManagementId,
          buildingName,
          buildingAddress,
          autoFulltechOfficeId,
          memo,
          isConnectionAlertEnabled,
          connectionAlertEmail,
          connectionRecoveryAlertPeriod:
            connectionRecoveryAlertPeriodMinutes * 60,
          connectionAlertPeriod: connectionAlertPeriodMinutes * 60,
          connectionReminderAlertPeriod:
            connectionReminderAlertPeriodMinutes * 60,
          isAnomalyAlertEnabled,
          anomalyAlertEmail,
          isAnomalyUnresponsiveOpenAlertEnabled,
          isAnomalyUnresponsiveLockAlertEnabled,
          isDoorStackedAlertEnabled,
          isCannotAllCloseAlertEnabled,
          isCannotAnswerBackOnAlertEnabled,
          isCannotAnswerBackOffAlertEnabled,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  const deleteBuildingData = async () => {
    const jwtToken = await getJwtTokenFromIdToken();

    // call deleteBuilding API
    const response = await fetch(
      process.env.REACT_APP_API_ENDPOINT + `/buildings/${autoBuildingId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: jwtToken, // Authorizationヘッダーを追加
        },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  };

  const sendTestEmail = async (alertType: "connection" | "anomaly") => {
    const jwtToken = await getJwtTokenFromIdToken();
    const emailToSend =
      alertType === "connection" ? connectionAlertEmail : anomalyAlertEmail;

    // call sendTestAlertEmail API
    const response = await fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/buildings/${autoBuildingId}/test-alert-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          alertType: alertType,
          toAddress: emailToSend,
        }),
      }
    );

    if (response.ok) {
      const result = await response.json();
      console.log("メールテストリザルト" + JSON.stringify(result));
      setSnackbarMessage(
        "メールアドレスの送信テストを行いました。入力したメールアドレスにテストメールが届いていればテスト完了です。"
      );
      setAlertSeverity("success");
      setSnackbarOpen(true);
    } else {
      console.error("サーバーエラーが発生しました。");
      setSnackbarMessage("サーバーエラーが発生しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row" colSpan={2}>
                    <Typography variant="h6">物件情報</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>物件システムID</TableCell>
                  <TableCell>
                    <Typography>{buildingId}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>取付先コード</TableCell>
                  <TableCell>
                    <TextField
                      label="取付先名（物件名称）"
                      value={buildingManagementId}
                      size="small"
                      onChange={(e) => setBuildingManagementId(e.target.value)}
                      sx={{ width: "560px" }}
                      onBlur={() => handleBlur("buildingManagementId")}
                      helperText={validationErrors.buildingManagementId}
                      error={validationErrors.buildingManagementId !== ""}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    取付先名（物件名称）
                    <RequiredLabel />
                  </TableCell>
                  <TableCell>
                    <TextField
                      label="取付先名（物件名称）"
                      value={buildingName}
                      size="small"
                      onChange={(e) => setBuildingName(e.target.value)}
                      sx={{ width: "560px" }}
                      onBlur={() => handleBlur("buildingName")}
                      helperText={validationErrors.buildingName}
                      error={validationErrors.buildingName !== ""}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    物件住所
                    <RequiredLabel />
                  </TableCell>
                  <TableCell>
                    <TextField
                      label="物件住所"
                      value={buildingAddress}
                      size="small"
                      onChange={(e) => setBuildingAddress(e.target.value)}
                      sx={{ width: "560px" }}
                      onBlur={() => handleBlur("buildingAddress")}
                      helperText={validationErrors.buildingAddress}
                      error={validationErrors.buildingAddress !== ""}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    管轄拠点
                    <RequiredLabel />
                  </TableCell>
                  <TableCell>
                    <Box display="flex">
                      <Typography display={"flex"} alignItems={"center"}>
                        {fulltechOfficeName}
                      </Typography>
                      <Button onClick={() => setBaseModalOpen(true)}>
                        選択
                      </Button>
                      <BaseSelectionModal
                        baseModalOpen={baseModalOpen}
                        setBaseModalOpen={setBaseModalOpen}
                        setFulltechOfficeName={setFulltechOfficeName}
                        setFulltechOfficeId={setFulltechOfficeId}
                      />
                    </Box>
                    {validationErrors.fulltechOfficeId && (
                      <Typography color="error" variant="body2">
                        {validationErrors.fulltechOfficeId}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>登録日</TableCell>
                  <TableCell>
                    <Typography>
                      {createdAt ? formatDate(createdAt) : createdAt}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>メモ</TableCell>
                  <TableCell>
                    <TextField
                      label="メモ"
                      multiline
                      rows={2}
                      value={memo}
                      size="small"
                      onChange={(e) => setMemo(e.target.value)}
                      sx={{ width: "560px" }}
                      onBlur={() => handleBlur("memo")}
                      helperText={validationErrors.memo}
                      error={validationErrors.memo !== ""}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Grid paddingLeft={2} paddingRight={2} paddingBottom={3}>
            {/* 死活監視設定 */}
            <Box
              display="flex"
              alignItems="center"
              marginBottom={0}
              marginTop={4}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isConnectionAlertEnabled}
                    onChange={(e) =>
                      setConnectionAlertEnabled(e.target.checked)
                    }
                  />
                }
                label="死活監視"
              />
              <TextField
                label="通知先メールアドレス"
                variant="outlined"
                value={connectionAlertEmail}
                onChange={(e) => setConnectionAlertEmail(e.target.value)}
                style={{ margin: "0 16px", flexGrow: 1 }}
                size="small"
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => sendTestEmail("connection")}
              >
                メール送信テスト
              </Button>
            </Box>

            {/* テーブル: 接続設定 */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>名称</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>設定</TableCell>
                    <TableCell>補足</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ display: "none" }}>
                    <TableCell>接続検出感度</TableCell>
                    <TableCell>ER11001</TableCell>
                    <TableCell>
                      <TextField
                        select
                        value={connectionRecoveryAlertPeriodMinutes}
                        onChange={(e) =>
                          setConnectionRecoveryAlertPeriodMinutes(
                            Number(e.target.value)
                          )
                        }
                        size="small"
                        SelectProps={{
                          native: true,
                        }}
                      >
                        {Array.from({ length: 12 }, (_, i) => (i + 1) * 5).map(
                          (value) => (
                            <option key={value} value={value}>
                              {value} 分
                            </option>
                          )
                        )}
                      </TextField>
                    </TableCell>

                    <TableCell>5～60分 デフォルト値5分</TableCell>
                  </TableRow>
                  <TableRow sx={{ display: "none" }}>
                    <TableCell>切断検出感度</TableCell>
                    <TableCell>ER12001</TableCell>
                    <TableCell>
                      <TextField
                        select
                        value={connectionAlertPeriodMinutes}
                        onChange={(e) =>
                          setConnectionAlertPeriodMinutes(
                            Number(e.target.value)
                          )
                        }
                        size="small"
                        SelectProps={{
                          native: true,
                        }}
                      >
                        {Array.from({ length: 12 }, (_, i) => (i + 1) * 5).map(
                          (value) => (
                            <option key={value} value={value}>
                              {value} 分
                            </option>
                          )
                        )}
                      </TextField>
                    </TableCell>
                    <TableCell>5～60分 デフォルト値5分</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>切断継続通知</TableCell>
                    <TableCell>ER14001</TableCell>
                    <TableCell>
                      <TextField
                        select
                        value={connectionReminderAlertPeriodMinutes}
                        onChange={(e) =>
                          setConnectionReminderAlertPeriodMinutes(
                            Number(e.target.value)
                          )
                        }
                        size="small"
                        SelectProps={{
                          native: true,
                        }}
                      >
                        {connectionReminderAlertPeriodStepsDay.map((value) => {
                          if (value === 0) {
                            // 「オフ」の選択肢
                            return (
                              <option key={0} value={0}>
                                オフ
                              </option>
                            );
                          } else {
                            // 日単位の選択肢
                            return (
                              <option key={value} value={value}>
                                {value / 1440} 日
                              </option>
                            );
                          }
                        })}
                      </TextField>
                    </TableCell>
                    <TableCell>設定で指定した日数を超えた場合に通知</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* 異常動作設定 */}
            <Box
              display="flex"
              alignItems="center"
              marginBottom={0}
              marginTop={5}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAnomalyAlertEnabled}
                    onChange={(e) => setAnomalyAlertEnabled(e.target.checked)}
                  />
                }
                label="異常監視"
              />
              <TextField
                label="通知先メールアドレス"
                variant="outlined"
                value={anomalyAlertEmail}
                onChange={(e) => setAnomalyAlertEmail(e.target.value)}
                style={{ margin: "0 16px", flexGrow: 1 }}
                size="small"
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => sendTestEmail("anomaly")}
              >
                メール送信テスト
              </Button>
            </Box>

            {/* テーブル: 異常動作設定 */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>監視有効</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>トリガー</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={isDoorStackedAlertEnabled}
                        onChange={(e) =>
                          setIsDoorStackedAlertEnabled(e.target.checked)
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>ER20001</TableCell>
                    <TableCell>
                      ドアがスタックしている (全閉・全開にならない)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={isCannotAllCloseAlertEnabled}
                        onChange={(e) =>
                          setIsCannotAllCloseAlertEnabled(e.target.checked)
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>ER20002</TableCell>
                    <TableCell>
                      電気錠ON かつドアが全閉ではなくなった場合
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={isCannotAnswerBackOnAlertEnabled}
                        onChange={(e) =>
                          setIsCannotAnswerBackOnAlertEnabled(e.target.checked)
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>ER20003</TableCell>
                    <TableCell>
                      電気錠ON にしたのに、アンサーバックが ON にならない
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={isCannotAnswerBackOffAlertEnabled}
                        onChange={(e) =>
                          setIsCannotAnswerBackOffAlertEnabled(e.target.checked)
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>ER20004</TableCell>
                    <TableCell>
                      電気錠OFF にしたのに、アンサーバックが OFF にならない
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Paper>
        <Box display={"flex"} justifyContent={"flex-end"} marginTop={2}>
          <Button
            type="submit"
            variant="outlined"
            color="primary"
            onClick={() =>
              navigate(`/buildings/${autoBuildingId}/notification-log`)
            }
          >
            監視通知ログ
          </Button>
          <Button
            type="submit"
            variant="outlined"
            color="error"
            onClick={() => {
              setDeleteConfirmOpen(true);
            }}
            sx={{ marginLeft: 1 }}
            disabled={isDeleting} // 処理中はボタンを無効化
          >
            {isDeleting ? <CircularProgress size={24} /> : "削除"}
          </Button>
          <Button
            type="submit"
            variant="outlined"
            color="primary"
            sx={{ marginLeft: 1 }}
            onClick={async () => {
              if (!buildingName || !buildingAddress || !fulltechOfficeId) {
                setValidationErrors((prevErrors) => ({
                  ...prevErrors,

                  buildingName: !buildingName
                    ? "取付先名（物件名称）は必須です"
                    : prevErrors.buildingName,
                  buildingAddress: !buildingAddress
                    ? "物件住所は必須です"
                    : prevErrors.buildingAddress,
                  fulltechOfficeId: !fulltechOfficeId
                    ? "管轄拠点を選択してください"
                    : prevErrors.fulltechOfficeId,
                }));
                return; // エラーがあるため、更新操作を中断
              }

              // エラーがある場合、更新を阻止
              if (
                Object.values(validationErrors).some((error) => error !== "")
              ) {
                setSnackbarMessage("入力エラーがあります。修正してください。");
                setAlertSeverity("error");
                setSnackbarOpen(true);
                return;
              }
              setIsUpdating(true); // 処理中フラグを立てる
              try {
                const result = await patchBuildingData(
                  buildingManagementId,
                  buildingName,
                  buildingAddress,
                  fulltechOfficeId,
                  memo
                );
                console.log(result);
                setSnackbarMessage("更新完了しました。");
                setAlertSeverity("success");
                setSnackbarOpen(true);
              } catch (e) {
                console.error(e);
                setSnackbarMessage("更新失敗しました。");
                setAlertSeverity("error");
                setSnackbarOpen(true);
              } finally {
                setIsUpdating(false); // 処理終了
              }
            }}
            disabled={isUpdating} // 処理中はボタンを無効化
          >
            {isUpdating ? <CircularProgress size={24} /> : "更新"}
          </Button>
        </Box>
      </Grid>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
        }}
        onConfirm={async () => {
          setIsDeleting(true); // 処理中フラグを立てる
          try {
            const result = await deleteBuildingData();
            console.log(result);
            setDeletedConfirmOpen(true);
          } catch (e) {
            console.error(e);
            setSnackbarMessage("削除に失敗しました。");
            setAlertSeverity("error");
            setSnackbarOpen(true);
          } finally {
            setIsDeleting(false); // 処理終了
          }
        }}
        title="確認"
        description="物件に紐づくLAD契約者（オーナー）、SDC（ドア）は紐付けが解除されます。契約者と鍵情報は完全に削除され、元に戻すことはできません。本当に削除しますか？"
      />

      <ConfirmDialog
        open={deletedConfirmOpen}
        onClose={() => {
          setDeletedConfirmOpen(false);
        }}
        onConfirm={() => {
          // 物件削除後の画面遷移
          navigate(`/buildings`);
        }}
        title="確認"
        description="削除完了しました。"
        showCancelButton={false}
      />
    </Grid>
  );
};
